import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Box } from '@mui/material';
import { validateEmail, validateFirstName, validateLastName, validatePassword, validateUsername } from '../../utils/validators';
import { CreateUserModalProps } from '../types/type';

const CreateUserModal: React.FC<CreateUserModalProps> = ({ open, handleClose, handleCreateSubmit }) => {
  const [newUserData, setNewUserData] = useState({
    username: '',
    password: '',
    email: '',
    firstName: '',
    middleName: '',
    lastName: ''
  });
  const [validationErrors, setValidationErrors] = useState({
    username: '',
    password: '',
    email: '',
    firstName: '',
    lastName: ''
  });

  const handleNewUserChange = (field: keyof typeof newUserData, value: string) => {
    setNewUserData(prev => ({ ...prev, [field]: value }));
    let error: string | null = null;
    switch (field) {
      case 'username':
        ({ error } = validateUsername(value));
        break;
      case 'password':
        ({ error } = validatePassword(value));
        break;
      case 'email':
        ({ error } = validateEmail(value));
        break;
      case 'firstName':
        ({ error } = validateFirstName(value));
        break;
      case 'lastName':
        ({ error } = validateLastName(value));
        break;
      case 'middleName':
        break;
    }
    setValidationErrors(prev => ({ ...prev, [field]: error || '' }));
  };

  const handleSubmit = () => {
    const validations = {
      username: validateUsername(newUserData.username),
      password: validatePassword(newUserData.password),
      email: validateEmail(newUserData.email),
      firstName: validateFirstName(newUserData.firstName),
      lastName: validateLastName(newUserData.lastName)
    };

    const newErrors = {
      username: validations.username.error || '',
      password: validations.password.error || '',
      email: validations.email.error || '',
      firstName: validations.firstName.error || '',
      lastName: validations.lastName.error || ''
    };

    setValidationErrors(newErrors);

    if (Object.values(validations).some(validation => !validation.isValid)) {
      return;
    }

    handleCreateSubmit(newUserData);
    setNewUserData({
      username: '',
      password: '',
      email: '',
      firstName: '',
      middleName: '',
      lastName: ''
    });
  };

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle>Create New User</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1, width: 400 }}>
          <TextField
            fullWidth
            label="Username"
            value={newUserData.username}
            onChange={(e) => handleNewUserChange('username', e.target.value)}
            error={!!validationErrors.username}
            helperText={validationErrors.username}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
          />
          <TextField
            fullWidth
            label="Password"
            type="password"
            value={newUserData.password}
            onChange={(e) => handleNewUserChange('password', e.target.value)}
            error={!!validationErrors.password}
            helperText={validationErrors.password}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
          />
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={newUserData.email}
            onChange={(e) => handleNewUserChange('email', e.target.value)}
            error={!!validationErrors.email}
            helperText={validationErrors.email}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
          />
          <TextField
            fullWidth
            label="First Name"
            value={newUserData.firstName}
            onChange={(e) => handleNewUserChange('firstName', e.target.value)}
            error={!!validationErrors.firstName}
            helperText={validationErrors.firstName}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
          />
          <TextField
            fullWidth
            label="Middle Name" 
            value={newUserData.middleName}
            onChange={(e) => handleNewUserChange('middleName', e.target.value)}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
          />
          <TextField
            fullWidth
            label="Last Name"
            value={newUserData.lastName}
            onChange={(e) => handleNewUserChange('lastName', e.target.value)}
            error={!!validationErrors.lastName}
            helperText={validationErrors.lastName}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="inherit">
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" color="primary">
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateUserModal;